/**
 * Single source of truth for the Services Marketplace provider vocabulary:
 * category / lead-type / coverage-type enums and their human-readable labels.
 *
 * Consumed by:
 * - server schema + constants (`lib/db/src/schema/providers.ts`)
 * - web label maps (`artifacts/aero/src/lib/providers.ts`)
 * - mobile label maps (`artifacts/aero-mobile/lib/providers.ts`)
 *
 * Because every surface imports this one module, adding or renaming a
 * category/lead type/coverage type here updates web, mobile, and server in
 * lockstep — no surface can silently show raw enum values or stale names.
 * (Same pattern as `@workspace/us-regions` for the region→states map.)
 */
import { isUsCountryName, regionStatesForName } from "@workspace/us-regions";

/** The 19 service-marketplace provider categories (primary + secondary). */
export const PROVIDER_CATEGORIES = [
  "aircraft_sales",
  "aircraft_acquisition",
  "aircraft_brokerage",
  "aircraft_management",
  "charter_operator",
  "charter_broker",
  "aircraft_leasing",
  "fractional_ownership",
  "aircraft_insurance",
  "aviation_finance",
  "escrow_title",
  "aircraft_appraisals",
  "tax_legal",
  "aircraft_detailing",
  "pilot_services",
  "crew_staffing",
  "trip_support",
  "concierge",
  "training_consulting",
] as const;
export type ProviderCategory = (typeof PROVIDER_CATEGORIES)[number];

/** Human-readable labels for each provider category. */
export const PROVIDER_CATEGORY_LABELS: Record<ProviderCategory, string> = {
  aircraft_sales: "Aircraft Sales",
  aircraft_acquisition: "Aircraft Acquisition",
  aircraft_brokerage: "Aircraft Brokerage",
  aircraft_management: "Aircraft Management",
  charter_operator: "Charter Operator",
  charter_broker: "Charter Broker",
  aircraft_leasing: "Aircraft Leasing",
  fractional_ownership: "Fractional / Shared Ownership",
  aircraft_insurance: "Aircraft Insurance",
  aviation_finance: "Aviation Finance",
  escrow_title: "Escrow / Title",
  aircraft_appraisals: "Aircraft Appraisals",
  tax_legal: "Tax / Legal",
  aircraft_detailing: "Aircraft Detailing",
  pilot_services: "Pilot Services",
  crew_staffing: "Crew Staffing",
  trip_support: "Trip Support",
  concierge: "Concierge",
  training_consulting: "Training / Consulting",
};

/** Contextual quick-action lead/request types a provider can enable. */
export const PROVIDER_LEAD_TYPES = [
  "aircraft_sales_consultation",
  "acquisition_help",
  "management_consultation",
  "charter",
  "insurance_support",
  "finance_support",
  "legal_support",
  "appraisal",
  "contact",
] as const;
export type ProviderLeadType = (typeof PROVIDER_LEAD_TYPES)[number];

/** Human-readable button labels for each lead/request type (Title Case). */
export const PROVIDER_LEAD_TYPE_LABELS: Record<ProviderLeadType, string> = {
  aircraft_sales_consultation: "Request Aircraft Sales Consultation",
  acquisition_help: "Request Acquisition Help",
  management_consultation: "Request Aircraft Management Consultation",
  charter: "Request Charter",
  insurance_support: "Request Insurance Support",
  finance_support: "Request Finance Support",
  legal_support: "Request Legal Support",
  appraisal: "Request Appraisal",
  contact: "Contact Provider",
};

/** Geographic coverage scope. */
export const PROVIDER_COVERAGE_TYPES = [
  "global",
  "country",
  "regional",
  "state",
] as const;
export type ProviderCoverageType = (typeof PROVIDER_COVERAGE_TYPES)[number];

/** Human-readable labels for each coverage scope. */
export const PROVIDER_COVERAGE_LABELS: Record<ProviderCoverageType, string> = {
  global: "Global",
  country: "Country",
  regional: "Regional",
  state: "State",
};

/** Label for a category value, falling back to the raw value when unknown. */
export function categoryLabel(value: string): string {
  return PROVIDER_CATEGORY_LABELS[value as ProviderCategory] ?? value;
}

/** Label for a lead-type value, falling back to the raw value when unknown. */
export function leadTypeLabel(value: string): string {
  return PROVIDER_LEAD_TYPE_LABELS[value as ProviderLeadType] ?? value;
}

/** Label for a coverage-type value, falling back to the raw value when unknown. */
export function coverageLabel(value: string): string {
  return PROVIDER_COVERAGE_LABELS[value as ProviderCoverageType] ?? value;
}

/** How a provider can deliver service. */
export const SERVICE_AVAILABILITY_MODES = [
  "on_field",
  "mobile",
  "remote",
] as const;
export type ServiceAvailabilityMode =
  (typeof SERVICE_AVAILABILITY_MODES)[number];

export const COVERAGE_MATCH_TYPES = [
  "primary_airport",
  "served_airport",
  "radius",
  "state",
  "region",
  "country",
  "global",
] as const;
export type CoverageMatchType = (typeof COVERAGE_MATCH_TYPES)[number];

export interface CoverageTarget {
  airportCode: string;
  state?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface ProviderCoverage {
  primaryAirportCode?: string | null;
  servedAirportCodes?: readonly string[] | null;
  serviceRadiusNm?: number | null;
  availabilityModes?: readonly string[] | null;
  coverageType?: string | null;
  coverageStates?: readonly string[] | null;
  coverageRegions?: readonly string[] | null;
  coverageCountries?: readonly string[] | null;
  baseLatitude?: number | null;
  baseLongitude?: number | null;
}

export interface CoverageMatch {
  matchType: CoverageMatchType;
  matchReason: string;
  /** Only set for a radius match, where distance affects eligibility/ranking. */
  distanceNm?: number;
}

const upper = (value: string | null | undefined) =>
  value?.trim().toUpperCase() ?? "";
const includesUpper = (
  values: readonly string[] | null | undefined,
  value: string,
) => values?.some((item) => upper(item) === value) ?? false;

/** Great-circle distance in nautical miles. */
export function distanceNm(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
): number {
  const rad = Math.PI / 180;
  const dLat = (toLat - fromLat) * rad;
  const dLng = (toLng - fromLng) * rad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(fromLat * rad) *
      Math.cos(toLat * rad) *
      Math.sin(dLng / 2) ** 2;
  return 3440.065 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Conservative, pure coverage resolver shared by server and clients.
 * Precedence is contractual; business city/address are intentionally absent.
 */
export function matchProviderCoverage(
  provider: ProviderCoverage,
  target: CoverageTarget,
): CoverageMatch | null {
  const airport = upper(target.airportCode);
  if (!airport) return null;
  if (upper(provider.primaryAirportCode) === airport) {
    return {
      matchType: "primary_airport",
      matchReason: `Based at ${airport}`,
    };
  }
  if (includesUpper(provider.servedAirportCodes, airport)) {
    return {
      matchType: "served_airport",
      matchReason: `Serves ${airport}`,
    };
  }
  const modes = provider.availabilityModes ?? [];
  if (
    provider.serviceRadiusNm != null &&
    modes.some((mode) => ["mobile", "Mobile"].includes(mode)) &&
    provider.baseLatitude != null &&
    provider.baseLongitude != null &&
    target.latitude != null &&
    target.longitude != null
  ) {
    const distance = distanceNm(
      provider.baseLatitude,
      provider.baseLongitude,
      target.latitude,
      target.longitude,
    );
    if (distance <= provider.serviceRadiusNm + Number.EPSILON) {
      return {
        matchType: "radius",
        matchReason: `Within ${provider.serviceRadiusNm} NM mobile-service radius`,
        distanceNm: Math.round(distance * 10) / 10,
      };
    }
  }
  const state = upper(target.state);
  if (state && includesUpper(provider.coverageStates, state)) {
    return { matchType: "state", matchReason: `Serves ${state}` };
  }
  if (state) {
    const region = provider.coverageRegions?.find((name) =>
      regionStatesForName(name).includes(state),
    );
    if (region) {
      return { matchType: "region", matchReason: `Serves ${region.trim()}` };
    }
  }
  const country = upper(target.country);
  // Airport data canonically identifies the US as "US", while legacy
  // provider profiles have used several accepted US names. Other countries
  // deliberately remain exact-code comparisons rather than guessed aliases.
  const countryMatches =
    country === "US"
      ? provider.coverageCountries?.some(isUsCountryName)
      : includesUpper(provider.coverageCountries, country);
  if (country && countryMatches) {
    return { matchType: "country", matchReason: `Serves ${country}` };
  }
  if (provider.coverageType?.trim().toLowerCase() === "global") {
    return { matchType: "global", matchReason: "Worldwide service" };
  }
  return null;
}

export const COVERAGE_MATCH_PRECEDENCE: Record<CoverageMatchType, number> = {
  primary_airport: 0,
  served_airport: 1,
  radius: 2,
  state: 3,
  region: 4,
  country: 5,
  global: 6,
};
